import React from 'react';

import { useHistory } from 'react-router';

import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import {
  DesyncedItemLocation,
  DesyncedOptions,
} from '@recurrency/core-api-schema/dist/purchasing/getDesyncedItemLocations';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { Tooltip, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { theme } from 'theme';

import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { Container } from 'components/Container';
import { FlexSpace } from 'components/FlexSpace';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { PageHeader } from 'components/PageHeader';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { formatPercent } from 'utils/formatting';
import { routes } from 'utils/routes';
import { useSearchIndex } from 'utils/search/search';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { asKeyOf } from 'utils/tables';

import { StatusTile } from './StatusTile';

export const SyncStatusPage = () => {
  const history = useHistory();
  const desyncedTableProps = useCoreApiTableProps({
    schema: schemas.purchasing.getDesyncedItemLocations,
  });

  const searchResponse = useSearchIndex({
    indexName: SearchIndexName.Planning,
  });
  const totalRowCount = searchResponse?.data?.nbHits || 0;
  // Since there are two options for each item (min/max and replenishment method), we multiply the total row count by 2
  const totalAvailableOptions = totalRowCount * 2;
  const syncedPercentage = (totalAvailableOptions - desyncedTableProps.totalCount) / totalAvailableOptions;

  const syncTypeColors = {
    [DesyncedOptions.MinMax]: 'info' as const,
    [DesyncedOptions.ReplenishmentMethod]: 'warning' as const,
  };

  const syncTypeText = {
    [DesyncedOptions.MinMax]: 'Min/Max',
    [DesyncedOptions.ReplenishmentMethod]: 'Replenishment method',
  };

  const desyncedColomns: ColumnType<DesyncedItemLocation>[] = [
    {
      title: 'Sync Type',
      dataIndex: asKeyOf<DesyncedItemLocation>('desynced'),
      render: (desynced: DesyncedOptions) => (
        <Tooltip title={`Recurrency ${syncTypeText[desynced]} value does match the ERP`}>
          <Badge variant={syncTypeColors[desynced]} label={syncTypeText[desynced]} />
        </Tooltip>
      ),
    },
    {
      title: 'Item',
      dataIndex: asKeyOf<DesyncedItemLocation>('itemId'),
      render: (_: string, record: DesyncedItemLocation) => (
        <div>
          {record.itemCode}: {record.itemName}
        </div>
      ),
    },
    {
      title: 'Location',
      dataIndex: asKeyOf<DesyncedItemLocation>('locationId'),
      render: (locationId: string, record: DesyncedItemLocation) => (
        <div>
          {locationId}: {record.locationName}
        </div>
      ),
    },
    {
      title: 'ERP',
      render: (record: DesyncedItemLocation) =>
        record.desynced === 'min_max' ? `${record.erpMin}/${record.erpMax}` : record.erpReplenishmentMethod,
    },
    {
      title: 'Recurrency',
      render: (record: DesyncedItemLocation) =>
        record.desynced === 'min_max' ? `${record.currentMin}/${record.currentMax}` : record.replenishmentMethod,
    },
  ];

  return (
    <Container>
      <PageHeader
        title="Sync Status"
        subtitle={
          <>
            <>Recurrency and your ERP have different min/max values for the items below.</>
            <br />
            <>
              ERP values are used across the rest of the product. Recurrency values only appear here until you choose
              which system’s values to apply to both.
            </>
          </>
        }
        headerActions={
          <>
            <Button onClick={() => history.push(routes.monitoring.exportJobs())}>View Export Jobs</Button>
            <Button
              type="primary"
              permissionsRequired={[TenantUserPermission.ActionExportMinMax]}
              disabled={desyncedTableProps.totalCount === 0}
              onClick={async () => {
                const submitNotification = createSubmissionNotification({
                  entityName: 'Replenishment Settings',
                  expectedWaitSeconds: 60,
                  submittingMessage: 'Pushing from Recurrency to your ERP',
                  fireInProgressNotification: false,
                });
                const confirmed = await showAsyncModal(ConfirmModal, {
                  title: 'Push from Recurrency to ERP',
                  content: (
                    <FlexSpace direction="column" gap={16}>
                      <Typography.Text>
                        This will push the <strong>replenishment method</strong> and <strong>min/max values</strong> for
                        these items from Recurrency to your ERP.
                      </Typography.Text>
                      <Typography.Text>
                        <strong>This action cannot be undone.</strong> Are you sure you want to continue?
                      </Typography.Text>
                    </FlexSpace>
                  ),
                  okText: 'Push to ERP',
                  cancelText: 'Cancel',
                });
                if (confirmed) {
                  submitNotification.inProgress();
                  const result = await coreApiFetch(schemas.purchasing.postRetryExportDesyncedValues, {});
                  if (result.status !== 200) {
                    submitNotification.error('Failed to push Recurrency values to your ERP');
                  }
                  submitNotification.success({ successMessage: 'Replenishment settings pushed to your ERP' });
                }
              }}
            >
              Push from Recurrency to ERP
            </Button>
            <Button
              type="primary"
              permissionsRequired={[TenantUserPermission.ActionExportMinMax]}
              disabled={desyncedTableProps.totalCount === 0}
              onClick={async () => {
                const submitNotification = createSubmissionNotification({
                  entityName: 'Replenishment Settings',
                  expectedWaitSeconds: 5,
                  submittingMessage: 'Pulling from your ERP to Recurrency',
                  fireInProgressNotification: false,
                });
                const confirmed = await showAsyncModal(ConfirmModal, {
                  title: 'Pull from ERP to Recurrency',
                  content: (
                    <FlexSpace direction="column" gap={16}>
                      <Typography.Text>
                        This will pull the <strong>replenishment method</strong> and <strong>min/max values</strong> for
                        these items from your ERP to Recurrency.
                      </Typography.Text>
                      <Typography.Text>
                        <strong>This action cannot be undone.</strong> Are you sure you want to continue?
                      </Typography.Text>
                    </FlexSpace>
                  ),
                  okText: 'Pull from ERP',
                  cancelText: 'Cancel',
                });
                if (confirmed) {
                  submitNotification.inProgress();
                  const result = await coreApiFetch(schemas.purchasing.deleteReplenishmentSettings, {});
                  if (result.status !== 200) {
                    submitNotification.error('Failed to pull values from ERP');
                  }
                  submitNotification.success({ successMessage: 'Replenishment settings pulled from your ERP' });
                }
              }}
            >
              Pull from ERP to Recurrency
            </Button>
          </>
        }
      />
      <FlexSpace
        direction="row"
        gap={24}
        className={css`
          margin-bottom: 16px;
        `}
      >
        <StatusTile
          icon={<CheckCircleOutlined style={{ fontSize: 24, color: theme.colors.success[500] }} />}
          title="Sync Status"
          data={
            totalRowCount > 0
              ? formatPercent(
                  // Limit to 99.9% to avoid showing 100% when there are desynced items
                  desyncedTableProps.totalCount > 0 ? Math.min(syncedPercentage, 0.999) : syncedPercentage,
                  1,
                )
              : '-%'
          }
          description="Items in sync across ERP and Recurrency"
        />
        <StatusTile
          icon={<WarningOutlined style={{ fontSize: 24, color: theme.colors.warning[500] }} />}
          title="Requires Action"
          data={`${desyncedTableProps.totalCount}`}
          description="Items with mismatched min/max or replenishment method"
        />
      </FlexSpace>
      <AsyncTable tableProps={desyncedTableProps} columns={desyncedColomns} />
    </Container>
  );
};
